import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useId } from "react";
import { matchPath, useLocation } from "react-router-dom";

import type { MenuGroup } from "../../../navigation/menuConfig";
import { getModuleSidebarIconTone } from "../../../navigation/menuConfig";

import { SIDEBAR_HOVER_BG, SIDEBAR_MUTED_TEXT } from "./sidebarClassNames";
import { SidebarGroup } from "./SidebarGroup";

import { Button } from "@/components/Button";

type SidebarMenuProps = {
  groups: readonly MenuGroup[];
  collapsed: boolean;
  expanded: Record<string, boolean>;
  onToggleGroup: (groupId: string) => void;
  onItemClick?: () => void;
};

function SidebarMenuList({
  groups,
  collapsed,
  expanded,
  onToggleGroup,
  onItemClick,
}: SidebarMenuProps) {
  const menuId = useId();
  const { pathname } = useLocation();
  // This wrapper keeps the list rendering self-contained so a virtualized
  // viewport (react-window, react-virtual, etc.) can replace the mapping logic
  // later without reshaping the Sidebar API.
  return (
    <div className="space-y-1" data-menu-list>
      {groups.map((group) => {
        const key = group.id;
        const isExpanded = expanded[key] ?? false;
        const panelId = `${menuId}-sidebar-group-panel-${key}`;
        const GroupIcon = group.icon;
        const isActiveGroup = group.items.some(
          (item) =>
            matchPath({ path: item.href, end: false }, pathname) ||
            item.children?.some((child) =>
              matchPath({ path: child.href, end: false }, pathname),
            ),
        );

        if (collapsed && group.collapsible !== false) {
          return (
            <Popover key={key} className="flex justify-center py-1">
              {({ close }) => (
                <>
                  <PopoverButton
                    className={clsx(
                      "shell-icon !h-10 !w-10",
                      isActiveGroup &&
                        "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300",
                    )}
                    aria-label={`${group.name} navigation`}
                    title={group.name}
                  >
                    {GroupIcon ? (
                      <GroupIcon
                        className={clsx(
                          "h-5 w-5",
                          getModuleSidebarIconTone(key, isActiveGroup),
                        )}
                      />
                    ) : (
                      <span className="text-xs">{group.name.slice(0, 1)}</span>
                    )}
                  </PopoverButton>
                  <PopoverPanel
                    portal
                    anchor={{ to: "right start", gap: 12 }}
                    data-workspace-overlay
                    className="shell-popover z-50 max-h-[80dvh] w-64 overflow-y-auto py-3"
                  >
                    <p className="mb-2 px-5 text-xs font-semibold text-slate-500">
                      {group.name}
                    </p>
                    <SidebarGroup
                      group={group}
                      collapsed={false}
                      onItemClick={() => {
                        close();
                        onItemClick?.();
                      }}
                      showGroupLabel={false}
                    />
                  </PopoverPanel>
                </>
              )}
            </Popover>
          );
        }

        if (group.collapsible === false) {
          return (
            <div key={key} data-menu-group={key}>
              <SidebarGroup
                group={group}
                collapsed={collapsed}
                onItemClick={onItemClick}
                showGroupLabel={false}
              />
            </div>
          );
        }

        return (
          <div key={key} data-menu-group={key}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onToggleGroup(key)}
              className={clsx(
                "sidebar-parent-row w-full !justify-between",
                collapsed ? "px-2 py-2" : "px-4 py-2",
                "text-slate-700 dark:text-slate-200",
                SIDEBAR_HOVER_BG,
              )}
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? panelId : undefined}
              aria-label={collapsed ? `${group.name} navigation` : undefined}
              title={collapsed ? group.name : undefined}
            >
              <span
                className={clsx(
                  "flex items-center",
                  collapsed ? "justify-center w-8 h-8" : "gap-2",
                )}
              >
                {GroupIcon ? (
                  <GroupIcon
                    className={clsx(
                      "w-5 h-5 shrink-0",
                      getModuleSidebarIconTone(key, isActiveGroup),
                    )}
                    aria-hidden="true"
                  />
                ) : (
                  <span className="w-5 h-5" aria-hidden="true" />
                )}
                {!collapsed && <span className="text-xs font-medium">{group.name}</span>}
              </span>
              <ChevronDownIcon
                className={clsx(
                  "w-4 h-4 shrink-0 transition-transform",
                  isExpanded ? "rotate-180" : "rotate-0",
                )}
              />
            </Button>

            {isExpanded && (
              <div className="pb-1 pt-1" id={panelId} data-menu-group-body>
                <SidebarGroup
                  group={group}
                  collapsed={collapsed}
                  onItemClick={onItemClick}
                  showGroupLabel={false}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SidebarMenu({
  groups,
  collapsed,
  expanded,
  onToggleGroup,
  onItemClick,
}: SidebarMenuProps) {
  if (groups.length === 0) {
    return (
      <div className={clsx("py-4", collapsed ? "px-2" : "px-4")}>
        {collapsed ? (
          <div className={clsx("text-center text-[10px]", SIDEBAR_MUTED_TEXT)}>
            <span aria-hidden="true">—</span>
            <span className="sr-only">No navigation items available.</span>
          </div>
        ) : (
          <div role="status" className={clsx("text-xs", SIDEBAR_MUTED_TEXT)}>
            No navigation items available.
          </div>
        )}
      </div>
    );
  }

  return (
    <SidebarMenuList
      groups={groups}
      collapsed={collapsed}
      expanded={expanded}
      onToggleGroup={onToggleGroup}
      onItemClick={onItemClick}
    />
  );
}
